import { createElement, useMemo, forwardRef } from 'react';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { setAlpha } from '../../../design-tokens/utils';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { ButtonVariant } from '../../Button';

import { BaseSplitButton } from '../../SplitButton/__internal__/BaseSplitButton';
import { MultiBreadcrumbDesignProps } from '.';

import cx from 'classnames';
import selectClassMap from '../../Select/Select.css.json';
import '../../Select/Select.css';
import cn from './MultiBreadcrumbDesign.css.json';
import './MultiBreadcrumbDesign.css';

export const MultiBreadcrumbDesign = forwardRef<HTMLDivElement, MultiBreadcrumbDesignProps>(
  function MultiBreadcrumbDesign(props, ref) {
    const labels = useMemo(() => props.values.map((text) => ({ text })), [props.values]);
    const dt = useDesignTokens();
    const tokenOverrides = props.tokens?.();

    const setRef = useTruncationTooltip(ref);

    let variantStyle = {
      ...(dt.enableNovaTokens ? dt.stateStandardNova.normal : dt.stateStandard.normal),
      focus: 'var(--focus-light-bg)',
    };
    let buttonStyle;
    if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
      variantStyle = {
        fg: dt.c.colorTextInverse,
        bg: setAlpha(dt.c.colorTextInverse, 0.1),
        bd: dt.c.colorTextInverse,
        filter: dt.enableNovaTokens
          ? dt.stateStandardNova.normal.filter
          : dt.stateStandard.normal.filter,
        focus: 'var(--focus-dark-bg)',
      };
      buttonStyle = renderInlineCssVars({
        'button-hover-bg': setAlpha(dt.c.colorTextInverse, 0.15),
        'button-active-bg': setAlpha(dt.c.colorTextInverse, 0.25),
      });
    }

    const style = {
      ...renderInlineCssVars(
        {
          ...variantStyle,
          'focus-offset':
            dt.themeVariant === 'high-contrast'
              ? 'calc(-1 * var(--focus-width))'
              : 'calc(-1 * var(--focus-width) - 2px)',
        },
        'breadcrumb-dropdown'
      ),
      ...renderInlineCssVars(
        {
          'squish-xs': dt.spacing.insetSquish.xs.padding,
          'mg-xs': dt.spacing.core.xs,
          'anim-dur-enter': dt.animation.duration.enter.s,
          'anim-tf-enter': dt.animation.timingFunction.enter.m,
        },
        'breadcrumb'
      ),
      ...renderInlineCssVars(
        {
          'font-weight-thick': dt.enableNovaTokens
            ? dt.fontNova.weight.thick
            : dt.font.weight.thick,
          'font-family': tokenOverrides?.fontFamily ?? 'inherit',
        },
        'breadcrumb-text'
      ),
    };

    const buttonVariant =
      dt.themeVariant === 'light' && props.variant === 'primary-light'
        ? ButtonVariant.PrimaryIcon
        : ButtonVariant.Icon;

    return (
      <BaseSplitButton
        items={labels}
        text={labels[props.selectedIndex]?.text}
        onPress={() => props.onPress?.(props.index)}
        onOpen={() => {
          props.onOpen?.(props.index);
        }}
        onClose={() => {
          props.onClose?.(props.index);
        }}
        onItemSelected={(selectedIndex) => props.onPressItem?.(props.index, selectedIndex ?? 0)}
        focusable={props.focused}
        variant={buttonVariant}
        style={{ ...style, ...props.style }}
        ref={setRef}
        className={cx(
          selectClassMap.select,
          cn.dropdown,
          selectClassMap.label,
          cn.dropdownLabel,
          cn.itemText,
          cn.interactive,
          cn.opening
        )}
        ariaCurrent={props.isCurrent ? 'page' : undefined}
        testId={props.testId}
        ariaRoleDescription={''}
        buttonStyle={buttonStyle}
        buttonClassName={cx(cn.actionButton, props.isCurrent ? cn.current : undefined)}
        onFocus={props.onFocus}
        tokens={() => ({ filter: 'none', ...tokenOverrides })}
      />
    );
  }
);
